import { defineStore } from 'pinia'
import tariffAccessService from '@/services/tariffs/tariffAccess.service'
import tariffCatalogService from '@/services/tariffs/tariffCatalog.service'
import { normalizeCapabilityList, normalizeUsageList } from '@/services/tariffs/tariffValueNormalizer'
import {
    TARIFF_ACCESS_LIFECYCLE as LIFECYCLE,
    TARIFF_SUBJECT_MODES as SUBJECT_MODES,
    isQuizOwnedFeatureKey,
} from '@/constants/tariffFeatureKeys'
import { isQuizOwner } from '@/utils/quizAccess'

const MAX_SCHEDULE_DELAY_MS = 24 * 60 * 60 * 1000
const VISIBILITY_REFRESH_MIN_INTERVAL_MS = 60 * 1000

let pendingAccessFetch = null
let queuedForcedAccessFetch = null
let accessGeneration = 0
let validUntilTimer = null
let visibilityChangeHandler = null
let quizSubjectToken = 0

function createEmptyQuizSubject() {
    return {
        token: 0,
        quizKey: null,
        mode: SUBJECT_MODES.NONE,
        effectiveAccess: null,
        featureUsage: null,
    }
}

// Collaborator on someone else's shared quiz -> owner tariff. Owner fields are
// expected only in that case; for own/admin/public quizzes the backend returns
// null and the authenticated user's tariff keeps answering quiz.* gates.
export function buildQuizTariffSubject(rawQuiz) {
    if (!rawQuiz || isQuizOwner(rawQuiz)) {
        return { mode: SUBJECT_MODES.SELF, effectiveAccess: null, featureUsage: null }
    }

    const ownerAccess = rawQuiz.owner_effective_access
    if (!ownerAccess || !Array.isArray(ownerAccess.feature_values)) {
        return { mode: SUBJECT_MODES.OWNER_UNAVAILABLE, effectiveAccess: null, featureUsage: null }
    }

    return {
        mode: SUBJECT_MODES.OWNER,
        effectiveAccess: normalizeCapabilityList(ownerAccess.feature_values),
        // Missing usage keeps the Map absent so quantitative gates stay non-creatable.
        featureUsage: Array.isArray(rawQuiz.owner_feature_usage) ? normalizeUsageList(rawQuiz.owner_feature_usage) : null,
    }
}

export const useTariffStore = defineStore('tariff', {
    state: () => ({
        effectiveAccess: new Map(),
        validUntil: null,
        featureUsage: new Map(),
        catalog: [],
        accessLifecycle: LIFECYCLE.IDLE,
        isAccessLoaded: false,
        isAccessLoading: false,
        isCatalogLoading: false,
        lastAccessLoadedAt: null,
        lastCatalogLoadedAt: null,
        error: null,
        quizSubject: createEmptyQuizSubject(),
    }),

    getters: {
        isAccessReady(state) {
            return state.isAccessLoaded && !state.isAccessLoading
        },
        isInitialLoading(state) {
            return state.accessLifecycle === LIFECYCLE.LOADING
        },
        isRefreshing(state) {
            return state.accessLifecycle === LIFECYCLE.REFRESHING
        },
        hasAccessError(state) {
            return state.accessLifecycle === LIFECYCLE.ERROR
        },
        isQuizOwnerSubject(state) {
            return state.quizSubject.mode === SUBJECT_MODES.OWNER || state.quizSubject.mode === SUBJECT_MODES.OWNER_UNAVAILABLE
        },
        // Single entry point for "whose tariff answers this feature key".
        resolveTariffSubject(state) {
            return (featureKey) => {
                const userSubject = {
                    mode: SUBJECT_MODES.SELF,
                    accessMap: state.effectiveAccess,
                    usageMap: state.featureUsage,
                    isLoading: state.accessLifecycle === LIFECYCLE.LOADING,
                    isUnavailable: false,
                }

                if (!isQuizOwnedFeatureKey(featureKey)) {
                    return userSubject
                }

                switch (state.quizSubject.mode) {
                    case SUBJECT_MODES.PENDING:
                        return { mode: SUBJECT_MODES.PENDING, accessMap: null, usageMap: null, isLoading: true, isUnavailable: false }
                    case SUBJECT_MODES.OWNER:
                        return {
                            mode: SUBJECT_MODES.OWNER,
                            accessMap: state.quizSubject.effectiveAccess,
                            usageMap: state.quizSubject.featureUsage,
                            isLoading: false,
                            isUnavailable: false,
                        }
                    case SUBJECT_MODES.OWNER_UNAVAILABLE:
                        return { mode: SUBJECT_MODES.OWNER_UNAVAILABLE, accessMap: null, usageMap: null, isLoading: false, isUnavailable: true }
                    default:
                        return userSubject
                }
            }
        },
    },

    actions: {
        setUserTariffState(rawUser = {}) {
            this.effectiveAccess = normalizeCapabilityList(rawUser?.effective_access?.feature_values)
            this.validUntil = rawUser?.effective_access?.valid_until || null
            this.featureUsage = normalizeUsageList(rawUser?.feature_usage)
        },

        fetchEffectiveAccess({ force = false } = {}) {
            if (pendingAccessFetch) {
                if (!force) {
                    return pendingAccessFetch
                }

                if (!queuedForcedAccessFetch) {
                    const queued = pendingAccessFetch
                        .catch(() => {})
                        .then(() => {
                            if (queuedForcedAccessFetch !== queued) {
                                return
                            }
                            queuedForcedAccessFetch = null
                            return this.fetchEffectiveAccess({ force: true })
                        })
                    queuedForcedAccessFetch = queued
                }
                return queuedForcedAccessFetch
            }

            if (this.isAccessLoaded && !force) {
                return Promise.resolve()
            }

            this.accessLifecycle = this.isAccessLoaded ? LIFECYCLE.REFRESHING : LIFECYCLE.LOADING
            this.isAccessLoading = true
            this.error = null

            const generation = accessGeneration
            const request = (async () => {
                try {
                    const user = await tariffAccessService.fetchUserTariffState({ current: true })
                    if (generation !== accessGeneration) {
                        return
                    }
                    this.setUserTariffState(user)
                    this.isAccessLoaded = true
                    this.lastAccessLoadedAt = Date.now()
                    this.accessLifecycle = LIFECYCLE.READY
                    this.scheduleValidUntilRefresh()
                } catch (error) {
                    if (generation !== accessGeneration) {
                        return
                    }
                    this.accessLifecycle = LIFECYCLE.ERROR
                    this.error = error?.message || 'Failed to load tariff access'
                    throw error
                } finally {
                    if (generation === accessGeneration) {
                        this.isAccessLoading = false
                        pendingAccessFetch = null
                    }
                }
            })()

            pendingAccessFetch = request
            return request
        },

        async refreshEffectiveAccess() {
            await this.fetchEffectiveAccess({ force: true })
        },

        scheduleValidUntilRefresh() {
            this.clearScheduledRefresh()

            if (!this.validUntil) {
                return
            }

            const validUntilMs = new Date(this.validUntil).getTime()
            if (!Number.isFinite(validUntilMs)) {
                return
            }

            const remaining = validUntilMs - Date.now()
            const delay = Math.max(0, Math.min(remaining, MAX_SCHEDULE_DELAY_MS))

            validUntilTimer = setTimeout(() => {
                validUntilTimer = null

                if (Date.now() >= validUntilMs) {
                    this.refreshEffectiveAccess().catch(() => {})
                    return
                }

                this.scheduleValidUntilRefresh()
            }, delay)
        },

        clearScheduledRefresh() {
            if (validUntilTimer) {
                clearTimeout(validUntilTimer)
                validUntilTimer = null
            }
        },

        initLifecycleListeners() {
            if (visibilityChangeHandler || typeof document === 'undefined') {
                return
            }

            visibilityChangeHandler = () => {
                if (document.visibilityState !== 'visible') {
                    return
                }

                const staleForMs = this.lastAccessLoadedAt ? Date.now() - this.lastAccessLoadedAt : Infinity
                if (staleForMs > VISIBILITY_REFRESH_MIN_INTERVAL_MS) {
                    this.refreshEffectiveAccess().catch(() => {})
                }
            }

            document.addEventListener('visibilitychange', visibilityChangeHandler)
        },

        disposeLifecycleListeners() {
            this.clearScheduledRefresh()

            if (visibilityChangeHandler && typeof document !== 'undefined') {
                document.removeEventListener('visibilitychange', visibilityChangeHandler)
            }
            visibilityChangeHandler = null
        },

        async fetchCatalog({ requiredFeatureValues, campaignKey, isActive, isSubscribable, price, validityMonths, force = false } = {}) {
            if (requiredFeatureValues?.length || campaignKey) {
                return tariffCatalogService.fetchTariffCatalog({ requiredFeatureValues, campaignKey, isActive, isSubscribable, price, validityMonths })
            }

            if (this.isCatalogLoading) {
                return this.catalog
            }

            if (this.catalog.length && !force) {
                return this.catalog
            }

            this.isCatalogLoading = true

            try {
                this.catalog = await tariffCatalogService.fetchTariffCatalog({ isActive, isSubscribable, price, validityMonths, campaignKey })
                this.lastCatalogLoadedAt = Date.now()
                return this.catalog
            } finally {
                this.isCatalogLoading = false
            }
        },

        async fetchActiveSubscribableTariffs() {
            return tariffCatalogService.fetchTariffCatalog({ isActive: true, isSubscribable: true })
        },

        async fetchFreeMonthlyTariffs() {
            return tariffCatalogService.fetchTariffCatalog({
                isActive: true,
                isSubscribable: false,
                price: { eq: 0 },
                validityMonths: { eq: 1 },
                campaignKey: null,
            })
        },

        invalidate() {
            this.isAccessLoaded = false
        },

        // Called when a quiz editor starts loading a quiz. Returns a token that
        // later apply/clear calls must present, so a slow response for a quiz
        // the user already left cannot overwrite the current subject.
        beginQuizTariffSubject(quizKey) {
            quizSubjectToken += 1
            this.quizSubject = {
                ...createEmptyQuizSubject(),
                token: quizSubjectToken,
                quizKey: quizKey ?? null,
                mode: SUBJECT_MODES.PENDING,
            }
            return quizSubjectToken
        },

        applyQuizTariffSubject(rawQuiz, token) {
            if (token !== this.quizSubject.token) {
                return
            }
            this.quizSubject = {
                ...this.quizSubject,
                ...buildQuizTariffSubject(rawQuiz),
            }
        },

        // Quiz failed to load: we cannot tell whose tariff applies, so deny
        // quiz.* paid actions instead of falling back to the user's tariff.
        failQuizTariffSubject(token) {
            if (token !== this.quizSubject.token) {
                return
            }
            this.quizSubject = {
                ...this.quizSubject,
                mode: SUBJECT_MODES.OWNER_UNAVAILABLE,
                effectiveAccess: null,
                featureUsage: null,
            }
        },

        clearQuizTariffSubject(token) {
            if (token !== this.quizSubject.token) {
                return
            }
            this.quizSubject = createEmptyQuizSubject()
        },

        // Re-reads owner access/usage after a mutation that consumes owner quota
        // (e.g. quiz.custom_domain). No-op unless a collaborator subject is active.
        async refreshQuizTariffSubject() {
            const { token, quizKey } = this.quizSubject
            if (!this.isQuizOwnerSubject || !quizKey) {
                return
            }
            // On failure the last verified owner snapshot stays; the subject never
            // falls back to the viewer's tariff.
            const quiz = await tariffAccessService.fetchQuizOwnerTariffState({ code: quizKey })
            this.applyQuizTariffSubject(quiz, token)
        },

        // Refresh whichever tariff a quiz.* mutation consumed.
        refreshAccessForFeature(featureKey) {
            if (isQuizOwnedFeatureKey(featureKey) && this.isQuizOwnerSubject) {
                return this.refreshQuizTariffSubject()
            }
            return this.refreshEffectiveAccess()
        },

        resetTariffStore() {
            this.disposeLifecycleListeners()
            accessGeneration += 1
            pendingAccessFetch = null
            queuedForcedAccessFetch = null

            this.effectiveAccess = new Map()
            this.validUntil = null
            this.featureUsage = new Map()
            this.catalog = []
            this.accessLifecycle = LIFECYCLE.IDLE
            this.isAccessLoaded = false
            this.isAccessLoading = false
            this.isCatalogLoading = false
            this.lastAccessLoadedAt = null
            this.lastCatalogLoadedAt = null
            this.error = null
            quizSubjectToken += 1
            this.quizSubject = createEmptyQuizSubject()
        },
    },
})
